/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Spinner,
  Box,
} from '@ouster/webviz-react-components';

import { useRef } from 'react';

import { useStoreState, useStoreActions } from '../reducers';

export default function ConfirmDialog() {
  const {
    isOpen,
    isLoading,
    onConfirm,
    title,
    body,
    bullets,
    confirmLabel,
    cancelLabel,
    isDestructive,
  } = useStoreState((state) => state.confirmDialog);

  const { closeConfirmDialog, setIsLoading } = useStoreActions(
    (actions) => actions.confirmDialog
  );

  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
      closeConfirmDialog();
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={closeConfirmDialog}
      data-testid="confirm-dialog-root"
    >
      <AlertDialogOverlay>
        <AlertDialogContent width="80%" maxWidth="480px">
          <AlertDialogHeader
            fontSize="larger"
            fontWeight="bold"
            paddingBottom="1rem"
            borderBottom="1px solid"
            borderColor="gray.500"
            marginBottom="1rem"
            data-testid="confirm-dialog-header"
          >
            {title ?? 'Alert'}
          </AlertDialogHeader>

          <AlertDialogBody data-testid="confirm-dialog-body">
            {!isLoading ? (
              <Box display="grid" rowGap="1.2rem">
                {body || (bullets && bullets.length > 0) ? (
                  <>
                    {body && <Box lineHeight={1.5}>{body}</Box>}

                    {bullets && bullets.length > 0 && (
                      <Box
                        as="ul"
                        display="flex"
                        flexDirection="column"
                        gap="1rem"
                        paddingLeft="1.5rem"
                        listStyleType="disc"
                      >
                        {bullets.map((b) => (
                          <li key={b} style={{ lineHeight: '1.6rem' }}>
                            {b}
                          </li>
                        ))}
                      </Box>
                    )}

                    {isDestructive && (
                      <p style={{ lineHeight: '1.6rem', fontWeight: 500 }}>
                        This action cannot be undone.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p>Are you sure you want to continue?</p>
                    {isDestructive && (
                      <p style={{ lineHeight: '1.6rem', fontWeight: 500 }}>
                        This action cannot be undone.
                      </p>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box display="flex" justifyContent="left" columnGap={8}>
                <Spinner />
                Action in progress
              </Box>
            )}
          </AlertDialogBody>

          <AlertDialogFooter data-testid="confirm-dialog-footer">
            <Button
              variant="ghost"
              colorScheme="gray"
              ref={cancelRef}
              onClick={() => closeConfirmDialog()}
              isDisabled={isLoading}
              data-testid="confirm-dialog-cancel-btn"
            >
              {cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={isDestructive ? 'outline' : 'solid'}
              colorScheme={isDestructive ? 'red' : 'primary'}
              onClick={handleConfirm}
              ml={3}
              isLoading={isLoading}
              data-testid="confirm-dialog-confirm-btn"
            >
              {confirmLabel ?? 'Confirm'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
